import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { TransactionsList } from '@/components/TransactionsList'
import { ActivityFilters } from '@/components/ActivityFilters'
import { PullToRefresh } from '@/components/PullToRefresh'
import { EmptyActivity, EmptyTransaction } from '@/components/EmptyStates'
import { StickyHeaderNavigation } from '@/components'
import { ScrollToTopButton } from '@/components/ScrollToTopButton'
import { TransactionTypes, RangeDate } from '@/domain/transactions'
import { WalletCurrency } from '@/domain'
import { useActivityData } from '@/hooks/useActivityData'
import { useScrollToTop } from '@/hooks/useScrollToTop'
import { usePullToRefreshHandler } from '@/hooks/usePullToRefreshHandler'
import { getDisplayCurrencyCode } from '@/utils/getDisplayCurrencyCode'
import useOffsets from '@/hooks/useOffsets'

export const CurrencyActivitiesScreen: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const offsets = useOffsets()

  // Get currency data from navigation state (similar to RN version)
  const currency = location.state as WalletCurrency & {
    currencyFilter?: string
    displayCurrencyCode?: string
  }

  // Filter states
  const [searchValue, setSearchValue] = useState('')
  const [selectedTypes, setSelectedTypes] = useState<TransactionTypes[]>([])
  const [dateRange, setDateRange] = useState<RangeDate>({
    startDate: null,
    endDate: null,
  })

  // Fetch activity data filtered by currency (like RN version)
  const {
    transactions: allTransactions,
    transactionsQuery,
    isLoading,
    isFetched,
    isRefetching,
    isError,
    bothAreEmpty,
    refetch,
  } = useActivityData({
    searchValue,
    selectedTypes,
    dateRange,
    showRequests: false, // Don't show requests for currency-specific activity
    cryptoCode: currency?.code || currency?.currencyFilter, // Filter by currency code
  })

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = transactionsQuery

  // Use hook for "scroll to top" functionality (replica from RN)
  const { showBackToTop, scrollToTop } = useScrollToTop(allTransactions.length)

  // Handlers
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const { isRefreshing: isPullRefreshing, handlePullRefresh } =
    usePullToRefreshHandler(refetch)

  // Redirect to dashboard if no currency data
  useEffect(() => {
    if (!currency || (!currency.code && !currency.currencyFilter)) {
      navigate('/dashboard')
    }
  }, [currency, navigate])

  if (!currency || (!currency.code && !currency.currencyFilter)) {
    return null
  }

  const currencyCode = currency.code || currency.currencyFilter
  const displayName = getDisplayCurrencyCode(currencyCode || '')

  const hasActiveFilters =
    searchValue.trim() !== '' ||
    selectedTypes.length > 0 ||
    dateRange.startDate !== null ||
    dateRange.endDate !== null

  const isCompletelyEmpty =
    bothAreEmpty && !hasActiveFilters && isFetched && !isLoading && !isError

  // Show empty activity state when there are no transactions and no filters
  if (isCompletelyEmpty) {
    return (
      <BaseSafeFormView flex={1} bgColor="background.screen" className="h-full">
        <StickyHeaderNavigation title={`${displayName}`} />
        <BaseView flex={1} style={{ paddingTop: `${offsets.top + 60}px` }}>
          <EmptyActivity />
        </BaseView>
      </BaseSafeFormView>
    )
  }

  return (
    <BaseSafeFormView
      data-component="currency-activities-screen"
      flex={1}
      bgColor="background.screen"
      style={{
        height: '100vh',
      }}
    >
      <StickyHeaderNavigation title={`${displayName}`} />

      <PullToRefresh
        onRefresh={handlePullRefresh}
        isRefreshing={isPullRefreshing || isRefetching}
        style={{
          paddingTop: `${offsets.top + 70}px`,
        }}
      >
        <BaseView flex={1} px={13}>
          {/* Scrollable Content with proper top padding */}
          <div
            style={{
              paddingBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <BaseView>
              <ActivityFilters
                onSearchChange={setSearchValue}
                selectedTypes={selectedTypes}
                onTypesChange={setSelectedTypes}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </BaseView>

            {allTransactions.length === 0 && isLoading ? (
              <BaseView>
                <EmptyTransaction isLoadingTransactions={true} />
              </BaseView>
            ) : allTransactions.length === 0 && isError && !isLoading ? (
              <BaseView center py={40}>
                <BaseText
                  fontSize={16}
                  fontFamily="regular"
                  color="label.default"
                  textAlign="center"
                  style={{ opacity: 0.7 }}
                >
                  {t('translation:transactionsNotFound') ||
                    "Sorry, we couldn't find any transactions"}
                </BaseText>
              </BaseView>
            ) : (
              <TransactionsList
                transactions={allTransactions}
                isLoading={isLoading && allTransactions.length === 0}
                onLoadMore={handleLoadMore}
                hasNextPage={hasNextPage}
                isLoadingMore={isFetchingNextPage}
              />
            )}
          </div>
        </BaseView>
      </PullToRefresh>

      {/* Back to Top Button - use reusable component */}
      <ScrollToTopButton visible={showBackToTop} onPress={scrollToTop} />
    </BaseSafeFormView>
  )
}
